import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ShieldCheck,
  Package,
  Check,
  LayoutGrid,
  List,
  FolderTree,
  Layers,
} from 'lucide-react'
import { SearchInput } from '@/shared/components/ui/search-input'
import { ConsumerShell } from '@/consumer/components/consumer-shell'
import { ConsumerCategoriesView } from '@/consumer/components/register-categories-view'
import { ConsumerLinesView } from '@/consumer/components/register-lines-view'
import { useConsumerBranding } from '@/consumer/hooks/use-consumer-branding'
import { Button } from '@/shared/components/ui/button'
import { ProductGridCard } from '@/shared/components/ui/product-grid-card'
import { Image } from '@/shared/components/ui/image'
import { PaginationControls } from '@/admin/components/paginated-table'
import { ProductFilterMenu } from '@/shared/components/ui/product-filter-menu'

import { cn } from '@/shared/lib/utils'
import {
  useConsumerProductSearch,
  useConsumerProductFilters,
  useConsumerRegistrationMetadata,
  useConsumerRegister,
  useConsumerFileUpload,
} from '@/consumer/hooks/use-registration'
import type {
  ProductSearchResult,
  ProductAttributeValue,
  ProductFilterValue,
} from '@/consumer/hooks/use-registration'
import {
  useForm,
  normaliseRegistrationField,
  normaliseTemplateAttribute,
  FILE_FIELD_TYPES,
  defineField,
  formatDisplayValue,
  isDisplayEmpty,
} from '@/shared/lib/forms'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { buildFormFieldFromMeta } from '@/shared/lib/forms/generated-metadata'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import { useConsumerResourceLookupProviders } from '@/consumer/hooks/use-resource-lookup-providers'

import { toastApiError } from '@/shared/lib/api-error'
import { readStockConfig } from '@/shared/lib/value-stock-field'
import { useFormatters } from '@/shared/lib/formatting'
const STEP_LABELS = ['Product', 'Details', 'Done']

// Synthetic form-field code for the "collect phone when missing" field. Not a
// consumer EAV attribute - submitted as its own `consumer_phone_number` key.
const CONSUMER_PHONE_CODE = 'consumer_phone_number'

// Step-0 product view toggle. WHICH layouts are offered and which one opens
// first is tenant config (`Tenant.settings.consumer.product_picker`, served on
// the branding payload) - a tenant selling one product in many sizes wants
// `lines`, a flat catalogue has no lines to group by. The consumer's own last
// choice persists per browser and wins over the tenant default.
type RegisterViewMode = 'grid' | 'list' | 'categories' | 'lines'
const REGISTER_VIEW_MODES: RegisterViewMode[] = ['categories', 'grid', 'list', 'lines']
// Icon + label per layout, so the toggle renders whatever the tenant offers
// instead of a hardcoded three buttons.
const REGISTER_VIEW_META: Record<
  RegisterViewMode,
  { icon: typeof FolderTree; labelKey: string; label: string }
> = {
  categories: {
    icon: FolderTree,
    labelKey: 'register.viewByCategory',
    label: 'Group by category',
  },
  lines: { icon: Layers, labelKey: 'register.viewByLine', label: 'Group by product line' },
  grid: { icon: LayoutGrid, labelKey: 'register.gridView', label: 'Grid view' },
  list: { icon: List, labelKey: 'register.listView', label: 'List view' },
}
// Bumped v1 -> v2 when the layout list became tenant config. A consumer's
// remembered choice wins over the tenant default, so every returning consumer
// would otherwise stay on whatever they last used and never see the layout
// their tenant just switched on - which is the whole point of setting one.
// Dropping the old key hands the tenant default to everyone exactly once; the
// next explicit toggle persists again under v2.
const REGISTER_VIEW_STORAGE_KEY = 'raven:consumer:register-view:v2'
function readStoredRegisterView(): RegisterViewMode | null {
  try {
    const v = window.localStorage.getItem(REGISTER_VIEW_STORAGE_KEY)
    return REGISTER_VIEW_MODES.includes(v as RegisterViewMode)
      ? (v as RegisterViewMode)
      : null
  } catch {
    // localStorage can throw in private-mode / blocked-cookie contexts.
    return null
  }
}

export default function RegisterPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { formatDate, locale, currency } = useFormatters()
  const lookupProviders = useConsumerResourceLookupProviders()

  // Step state
  const [step, setStep] = useState(() => (params.get('product') ? 1 : 0))

  // Step 0: Product
  const [productSearch, setProductSearch] = useState('')
  // Product view toggle: categories (grouped) / grid / list. The last-used view
  // is restored from localStorage on mount and defaults to the grid on a first
  // visit; every switch is persisted so the layout sticks across sessions.
  const [userViewMode, setUserViewMode] = useState<RegisterViewMode | null>(
    () => readStoredRegisterView(),
  )
  // Tenant config decides the offered layouts and the starting one; the
  // consumer's stored choice only counts while the tenant still offers it (a
  // tenant that turns a layout off must not leave anyone stuck on it).
  const { data: branding } = useConsumerBranding()
  // WPS-138 #1: defence in depth - a tenant that does not offer consumer
  // self-registration (OC at go-live) must not reach the register flow even via
  // a typed/bookmarked URL. Only redirect once branding has positively resolved
  // the capability off, so we never bounce a tenant that does offer it during
  // the branding load.
  const selfRegistrationEnabled =
    branding?.feature_flags?.self_registration !== false
  useEffect(() => {
    if (!selfRegistrationEnabled) navigate('/', { replace: true })
  }, [selfRegistrationEnabled, navigate])
  const availableViews: RegisterViewMode[] =
    branding?.product_picker?.views ?? ['categories', 'grid', 'list']
  const tenantDefaultView: RegisterViewMode =
    branding?.product_picker?.default_view ?? 'grid'
  const viewMode: RegisterViewMode =
    userViewMode && availableViews.includes(userViewMode)
      ? userViewMode
      : tenantDefaultView
  const setViewMode = (mode: RegisterViewMode) => {
    setUserViewMode(mode)
    try {
      window.localStorage.setItem(REGISTER_VIEW_STORAGE_KEY, mode)
    } catch {
      // localStorage unavailable (private mode / blocked) - non-fatal.
    }
  }
  const [filters, setFilters] = useState<ProductFilterValue>({})
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [selectedProductUuid, setSelectedProductUuid] = useState<string | null>(
    params.get('product') ?? null
  )

  // Debounce the search term so browsing the catalogue and typing don't fire
  // a request on every keystroke (the query is always enabled now).
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(productSearch.trim()), 250)
    return () => clearTimeout(handle)
  }, [productSearch])

  // A new search term, filter change, or page size resets to the first page.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, pageSize, filters])

  // Filter facets (category / variant + product attributes) for the tenant.
  const { data: filterData } = useConsumerProductFilters()
  const facets = filterData?.facets ?? []

  // Data. An empty query returns the browsable catalogue, so results show on
  // mount (no forced typing); a term filters them. Paginated server-side.
  const { data: productPage, isPending: searchPending } =
    useConsumerProductSearch(debouncedSearch, page, pageSize, filters)
  const searchResults = productPage?.results ?? []
  const totalCount = productPage?.count ?? 0
  const isSearching = debouncedSearch.length > 0
  const {
    data: metadata,
    isLoading: metadataLoading,
    isError: metadataError,
  } = useConsumerRegistrationMetadata(selectedProductUuid)
  const registerMutation = useConsumerRegister()
  const uploadMutation = useConsumerFileUpload()

  // Auto-advance if product was in URL
  useEffect(() => {
    if (params.get('product') && selectedProductUuid) {
      setStep(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run only on mount
  }, [])

  // Normalise the three groups of backend field metadata into canonical
  // FormFieldMeta. All three render in the one Details form; on submit the
  // values are partitioned back into the payload keys the backend expects.
  //  - holding fields      -> attribute_values          (HOLDING product attrs)
  //  - transaction fields  -> transaction_data          (purchase date/price/…)
  //  - consumer fields     -> consumer_attribute_values (e.g. mailing address)
  const holdingFieldMetas: FormFieldMeta[] = useMemo(
    () => (metadata?.fields ?? [])
      .map(normaliseRegistrationField)
      .filter((f) => !f.configuration_data?.hidden),
    [metadata?.fields],
  )

  const transactionFieldMetas: FormFieldMeta[] = useMemo(
    () => (metadata?.transaction_template?.attributes ?? [])
      .map(normaliseTemplateAttribute)
      // Templates sometimes mis-tag receipts; force the proof-of-purchase
      // use case on every file field so uploads are accepted (mirrors the
      // admin purchase step).
      .map((attr) =>
        FILE_FIELD_TYPES.has(attr.field_type?.toUpperCase())
          ? { ...attr, file_use_case: 'PROOF_OF_PURCHASE' }
          : attr,
      )
      .filter((f) => !f.configuration_data?.hidden),
    [metadata?.transaction_template],
  )

  // Host (vehicle / property) fields, sent as `host_data` on submit.
  const hostFieldMetas: FormFieldMeta[] = useMemo(
    () => (metadata?.host_template?.attributes ?? [])
      .map(normaliseTemplateAttribute)
      .filter((f) => !f.configuration_data?.hidden),
    [metadata?.host_template],
  )
  const hostCodes = useMemo(
    () => new Set(hostFieldMetas.map((f) => f.code_name)),
    [hostFieldMetas],
  )

  const consumerFieldMetas: FormFieldMeta[] = useMemo(
    () => (metadata?.consumer_fields ?? [])
      .map(normaliseTemplateAttribute)
      .filter((f) => !f.configuration_data?.hidden),
    [metadata?.consumer_fields],
  )

  const transactionCodes = useMemo(
    () => new Set(transactionFieldMetas.map((f) => f.code_name)),
    [transactionFieldMetas],
  )
  const transactionFieldTypes = useMemo(
    () => new Map(transactionFieldMetas.map((f) => [f.code_name, f.field_type])),
    [transactionFieldMetas],
  )
  const consumerCodes = useMemo(
    () => new Set(consumerFieldMetas.map((f) => f.code_name)),
    [consumerFieldMetas],
  )

  // Collect the consumer's phone during registration only when they have none
  // on file (onboarding does not capture it). Persisted onto the profile on
  // submit; if a phone already exists, this field is absent.
  const capturePhone = !!metadata?.capture_phone
  const phoneFieldMeta: FormFieldMeta | null = useMemo(
    () =>
      capturePhone
        ? {
            code_name: CONSUMER_PHONE_CODE,
            display_name: t('register.phoneNumber', 'Phone Number'),
            field_type: 'PHONE',
            required: true,
            hint: t('register.phoneNumberHint', 'Phone number'),
            configuration_data: {},
          }
        : null,
    [capturePhone, t],
  )

  // Consumer-only address relabel: the register form reads the address field's
  // label from the `register.customerAddressLabel` translation (default "Address") so a
  // tenant can show e.g. "Your Address" via a translation override - without
  // changing the attribute display_name the admin/CES customer card uses. Only
  // the address field is remapped; every other field keeps its backend label.
  const consumerFieldMetasLabeled = useMemo(
    () =>
      consumerFieldMetas.map((f) =>
        f.code_name === 'address'
          ? { ...f, display_name: t('register.customerAddressLabel', 'Address') }
          : f,
      ),
    [consumerFieldMetas, t],
  )

  const allFieldMetas = useMemo(
    () => [
      ...hostFieldMetas,
      ...holdingFieldMetas,
      ...transactionFieldMetas,
      ...consumerFieldMetasLabeled,
      ...(phoneFieldMeta ? [phoneFieldMeta] : []),
    ],
    [
      hostFieldMetas,
      holdingFieldMetas,
      transactionFieldMetas,
      consumerFieldMetasLabeled,
      phoneFieldMeta,
    ],
  )

  // Prefill returning consumers: saved attribute values (e.g. address) and,
  // when captured here, their existing phone. Non-profile fields get undefined.
  const prefillValues = useMemo<Record<string, unknown>>(() => {
    const map: Record<string, unknown> = {
      ...(metadata?.consumer_attribute_values ?? {}),
    }
    if (capturePhone && metadata?.consumer_phone) {
      map[CONSUMER_PHONE_CODE] = metadata.consumer_phone
    }
    const labelValue = params.get('value')
    const labelAttribute = params.get('attribute')
    // Label links can fill the serial. Nothing else from the query string.
    if (labelValue) {
      const stockField = holdingFieldMetas.find(
        (field) =>
          readStockConfig(field.configuration_data) &&
          (!labelAttribute || field.code_name === labelAttribute),
      )
      if (stockField) map[stockField.code_name] = labelValue
    }
    return map
  }, [
    metadata?.consumer_attribute_values,
    metadata?.consumer_phone,
    capturePhone,
    holdingFieldMetas,
    params,
  ])

  const generatedFields = useMemo(
    () =>
      allFieldMetas.map((field) =>
        buildFormFieldFromMeta(field, prefillValues[field.code_name]),
      ),
    [allFieldMetas, prefillValues],
  )
  const { Form: RegistrationForm } = useForm(
    {
      name: 'consumer-register-form',
      submit: () => null,
      fields: generatedFields,
      uploadFile: ({ file, use_case }) => uploadMutation.mutateAsync({ file, use_case }),
    },
    async (values) => {
      if (!selectedProductUuid) return

      try {
        const attributeValues: Record<string, unknown> = {}
        const consumerAttributeValues: Record<string, unknown> = {}
        const transactionData: Record<string, unknown> = {}
        const hostData: Record<string, unknown> = {}
        let consumerPhoneNumber = ''

        for (const [code, value] of Object.entries(values)) {
          if (code === CONSUMER_PHONE_CODE) {
            consumerPhoneNumber = String(value ?? '').trim()
            continue
          }
          if (transactionCodes.has(code)) {
            // MONEY fields produce a `{ amount, currency }` object; the
            // backend stores the numeric amount and dual-writes the currency
            // for the known `purchase_price` field.
            if (transactionFieldTypes.get(code) === 'MONEY') {
              const money = value as { amount?: number; currency?: string } | undefined
              if (money?.amount != null) transactionData[code] = money.amount
              if (money?.currency && code === 'purchase_price') {
                transactionData.purchase_currency = money.currency
              }
            } else {
              transactionData[code] = value
            }
          } else if (consumerCodes.has(code)) {
            consumerAttributeValues[code] = value
          } else if (hostCodes.has(code)) {
            hostData[code] = value
          } else {
            attributeValues[code] = value
          }
        }

        const payload: Record<string, unknown> = {
          product_uuid: selectedProductUuid,
          attribute_values: attributeValues,
        }
        if (Object.keys(consumerAttributeValues).length > 0) {
          payload.consumer_attribute_values = consumerAttributeValues
        }
        if (consumerPhoneNumber) {
          payload.consumer_phone_number = consumerPhoneNumber
        }
        if (Object.keys(transactionData).length > 0) {
          payload.transaction_data = transactionData
        }
        if (Object.keys(hostData).length > 0) {
          payload.host_data = hostData
        }
        const templates = metadata?.warranty_templates ?? []
        if (templates.length > 0) {
          const first = templates[0]
          payload.warranty_template_option_uuid =
            first.pre_assigned_option_uuid ?? first.options[0]?.uuid
        }

        const result = await registerMutation.mutateAsync(payload as Record<string, unknown>)
        if (result.warnings?.length) {
          toast.warning(result.warnings[0])
        }
        setStep(2)
      } catch (err) {
        toastApiError(err, 'Registration failed. Please try again.');
      }
    },
    [
      selectedProductUuid,
      allFieldMetas,
      transactionCodes,
      transactionFieldTypes,
      consumerCodes,
      hostCodes,
    ],
  )

  const warrantyTemplates = metadata?.warranty_templates ?? []
  const product = metadata?.product ?? null

  // Consumer-visible product specs (PRODUCT / variant-level attribute values)
  // for the summary card. Drop empty values so the card only shows real specs.
  const productAttributes: ProductAttributeValue[] = useMemo(
    () => (metadata?.product_attributes ?? []).filter((a) => !isDisplayEmpty(a.value)),
    [metadata?.product_attributes],
  )

  function handleSelectProduct(p: ProductSearchResult) {
    setSelectedProductUuid(p.uuid)
    setStep(1)
  }

  return (
    <ConsumerShell>
      {/* Step indicator bar */}
      <div className="max-w-2xl mx-auto mb-6">
        <div className="flex items-center mb-4">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex items-center">
              <div className="flex items-center gap-1.5">
                <span
                  className={cn(
                    'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[9px] font-normal',
                    i <= step
                      ? 'bg-[var(--green)] text-white'
                      : 'border-[0.5px] border-[var(--rule-strong)] bg-transparent text-[var(--ink-3)]',
                  )}
                >
                  {i < step ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className="text-[10px] uppercase tracking-[0.12em]">
                  {label}
                </span>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div className="mx-2 h-[0.5px] w-10 shrink-0 bg-[var(--rule-strong)]" />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-2xl mx-auto">
        {/* ── Step 0: Product browse + search ── */}
        {step === 0 && (
          <div>
            <h2 className="clara-heading mb-4">
              Find your product
            </h2>
            <div className="mb-4 flex items-center gap-2">
              <div className="flex-1">
                <SearchInput
                  value={productSearch}
                  onChange={setProductSearch}
                  placeholder={t('register.searchPlaceholder', {
                    defaultValue: 'Search by product name or model...',
                  })}
                  iconSize={16}
                  className="w-full text-[13px]"
                  autoFocus
                />
              </div>
              {/* Filter menu */}
              <ProductFilterMenu facets={facets} value={filters} onChange={setFilters} />
              {/* Categories / grid / list view toggle (all always available;
                  the chosen view is remembered per browser). */}
              <div className="flex shrink-0 items-center gap-0.5 rounded-[8px] border-[0.5px] border-solid border-[var(--rule)] p-0.5">
                {availableViews.map((mode) => {
                  const meta = REGISTER_VIEW_META[mode]
                  const Icon = meta.icon
                  return (
                    <Button
                      key={mode}
                      type="button"
                      variant={viewMode === mode ? 'secondary' : 'ghost'}
                      size="icon-sm"
                      aria-label={t(meta.labelKey, { defaultValue: meta.label })}
                      aria-pressed={viewMode === mode}
                      onClick={() => setViewMode(mode)}
                      className="cursor-pointer"
                    >
                      <Icon className="h-4 w-4" />
                    </Button>
                  )
                })}
              </div>
            </div>

            {/* Grouped category browse. A search bypasses grouping (a search is
                a "find a product" action) and falls through to the flat,
                filtered result list, exactly like the admin library. From the
                lines view it stays a list: cards cut long names short, and
                sizes of one line then look identical. */}
            {viewMode === 'categories' && !isSearching ? (
              <ConsumerCategoriesView onSelect={handleSelectProduct} />
            ) : viewMode === 'lines' && !isSearching ? (
              <ConsumerLinesView onSelect={handleSelectProduct} />
            ) : (
              <>
            {searchPending ? (
              <p className="py-12 text-center text-sm text-[var(--ink-3)]">
                {t('register.loadingProducts', { defaultValue: 'Loading products...' })}
              </p>
            ) : !searchResults?.length ? (
              <div className="text-center py-12">
                <Package className="mx-auto mb-3 h-10 w-10 text-[var(--ink-3)] opacity-40" />
                <p className="text-sm text-[var(--ink-3)]">
                  {isSearching
                    ? t('register.noProductsMatch', {
                        defaultValue: 'No products match your search',
                      })
                    : t('register.noProductsAvailable', {
                        defaultValue: 'No products available to register yet',
                      })}
                </p>
              </div>
            ) : viewMode !== 'list' && viewMode !== 'lines' ? (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {searchResults.map((p) => (
                  <ProductGridCard
                    key={p.uuid}
                    testId={`consumer-product-item-${p.uuid}`}
                    title={p.cached_short_name || p.cached_long_name}
                    subtitle={p.product_template.name}
                    imageUrl={p.image?.file_url}
                    thumbnailUrl={p.image?.thumbnail_url}
                    onClick={() => handleSelectProduct(p)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {searchResults.map((p) => (
                  <Button
                    variant="ghost"
                    key={p.uuid}
                    data-testid={`consumer-product-item-${p.uuid}`}
                    onClick={() => handleSelectProduct(p)}
                    className="flex items-center justify-start gap-3 p-3 rounded-[8px] transition-colors text-left cursor-pointer h-auto w-full border-[0.5px] border-solid border-[var(--rule)] bg-[var(--bg)] hover:bg-[var(--bg-subtle)]"
                  >
                    <Image
                      src={p.image?.file_url}
                      thumbnailSrc={p.image?.thumbnail_url}
                      alt=""
                      className="h-12 w-12 shrink-0 rounded-[4px] bg-[var(--bg-subtle)]"
                      fallback={<Package className="h-5 w-5 text-[var(--ink-3)]" />}
                    />
                    <div>
                      <p className="text-sm font-normal text-[var(--ink)]">
                        {p.cached_short_name || p.cached_long_name}
                      </p>
                      <p className="text-xs text-[var(--ink-3)]">
                        {p.product_template.name}
                      </p>
                    </div>
                  </Button>
                ))}
              </div>
            )}

            {/* Pagination (hidden by the control itself when empty). */}
            <PaginationControls
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
              </>
            )}
          </div>
        )}

        {/* ── Step 1: Registration Details ── */}
        {step === 1 && (
          <div>
            {metadataLoading ? (
              <p className="py-8 text-center text-sm text-[var(--ink-3)]">Loading...</p>
            ) : metadataError || !product ? (
              <div className="py-12 text-center">
                <Package className="mx-auto mb-3 h-10 w-10 text-[var(--ink-3)] opacity-40" />
                <p className="text-sm text-[var(--ink-3)]">
                  {t('register.productNotFound', {
                    defaultValue: 'Product not found',
                  })}
                </p>
              </div>
            ) : (
              <>
                {/* Product summary card */}
                <div className="mb-6 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-3">
                  <div className="flex items-center gap-3">
                    <Image
                      src={product.image?.file_url}
                      thumbnailSrc={product.image?.thumbnail_url}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded-[4px] bg-[var(--bg-subtle)]"
                      fallback={<Package className="h-5 w-5 text-[var(--ink-3)]" />}
                    />
                    <div>
                      <p className="text-sm font-normal text-[var(--ink)]">
                        {product.cached_short_name || product.cached_long_name}
                      </p>
                    </div>
                  </div>

                  {/* Consumer-visible product specs, driven by product metadata. */}
                  {productAttributes.length > 0 && (
                    <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 border-t-[0.5px] border-t-[var(--rule)] pt-3 sm:grid-cols-2">
                      {productAttributes.map((attr) => (
                        <div
                          key={attr.code_name}
                          className="flex items-baseline justify-between gap-3"
                        >
                          <dt className="text-xs text-[var(--ink-3)]">
                            {attr.display_name}
                          </dt>
                          <dd className="text-right text-xs font-normal text-[var(--ink)]">
                            {formatDisplayValue(
                              defineField({
                                code_name: attr.code_name,
                                display_name: attr.display_name,
                                field_type:
                                  attr.field_type as FormFieldMeta['field_type'],
                              }),
                              attr.value,
                              { locale, currency },
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>

                <h2 className="clara-heading mb-4">Registration details</h2>

                <ResourceLookupContextProvider providers={lookupProviders}>
                  <RegistrationForm className="flex flex-col gap-5">
                    {() => (
                      <div className="mt-5 flex flex-col gap-5">
                        {generatedFields.length === 0 && (
                          <p className="py-4 text-center text-sm text-[var(--ink-3)]">
                            No additional details required.
                          </p>
                        )}

                        {warrantyTemplates.length > 0 && (
                          <div className="flex items-center gap-3 rounded-[8px] border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] p-4">
                            <ShieldCheck className="h-5 w-5 shrink-0 text-[var(--green)]" />
                            <div>
                              <p className="text-sm font-normal text-[var(--ink)]">
                                {warrantyTemplates[0].name}
                              </p>
                              {warrantyTemplates[0].options[0] && (
                                <p className="text-xs text-[var(--ink-2)]">
                                  {warrantyTemplates[0].options[0].is_lifetime
                                    ? 'Lifetime coverage'
                                    : warrantyTemplates[0].options[0]
                                        .display_name ||
                                      `${warrantyTemplates[0].options[0].duration_in_months} months coverage`}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        <Button
                          type="submit"
                          disabled={registerMutation.isPending}
                          size="lg"
                          className="w-full cursor-pointer"
                        >
                          {registerMutation.isPending
                            ? 'Registering...'
                            : 'Register Product'}
                        </Button>
                      </div>
                    )}
                  </RegistrationForm>
                </ResourceLookupContextProvider>
              </>
            )}
          </div>
        )}

        {/* ── Step 2: Confirmation ── */}
        {step === 2 && registerMutation.data && (
          <div className="text-center py-8">
            <div
              className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[var(--green-bg)]"
            >
              <Check className="h-8 w-8 text-[var(--green)]" />
            </div>
            <h2 className="clara-heading mb-2">
              Product Registered
            </h2>
            <p className="mb-6 text-sm text-[var(--ink-3)]">
              {registerMutation.data.warranty
                ? 'Your warranty is now active.'
                : 'Your product has been registered successfully.'}
            </p>

            {/* Warranty card */}
            {registerMutation.data.warranty && (
              <div
                className="mx-auto max-w-sm rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-6 text-left"
              >
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck className="h-5 w-5 text-[var(--green)]" />
                  <span className="text-sm font-normal text-[var(--ink)]">
                    {registerMutation.data.warranty.class_name}
                  </span>
                </div>
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                  <span className="text-[var(--ink-3)]">Product</span>
                  <span className="font-normal text-[var(--ink)] text-right">
                    {registerMutation.data.holding.product.cached_short_name}
                  </span>
                  <span className="text-[var(--ink-3)]">Duration</span>
                  <span className="font-normal text-[var(--ink)] text-right">
                    {registerMutation.data.warranty.option_display_name}
                  </span>
                  <span className="text-[var(--ink-3)]">Active from</span>
                  <span className="font-normal text-[var(--ink)] text-right">
                    {formatDate(String(registerMutation.data.warranty.active_from))}
                  </span>
                  {registerMutation.data.warranty.expires_at && (
                    <>
                      <span className="text-[var(--ink-3)]">Expires</span>
                      <span className="font-normal text-[var(--ink)] text-right">
                        {formatDate(String(registerMutation.data.warranty.expires_at))}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            <Button
              onClick={() => navigate('/')}
              size="lg"
              className="mt-6 cursor-pointer px-6"
            >
              View My Products
            </Button>
          </div>
        )}
      </div>
    </ConsumerShell>
  );
}
